import type { Document } from "@contentful/rich-text-types"

import {
  documentToHtmlString,
  type Options,
} from "@contentful/rich-text-html-renderer"

/**
 * Accepts a variety of Contentful rich text shapes and returns a Document when possible.
 * - string: tries JSON.parse
 * - Document: returns as-is
 * - { raw: string | Document }: unwraps, then parses if needed
 * - { json: Document }: unwraps
 *
 * Returns null when it cannot produce a valid Document.
 */
export function ensureRichTextDocument(input: unknown): Document | null {
  if (input == null) {
    return null
  }

  // Already a Document?
  if (isRichTextDocument(input)) {
    return input
  }

  // Common Contentful wrappers
  if (typeof input === "object") {
    const obj = input as Record<string, unknown>

    if ("json" in obj && isRichTextDocument(obj.json)) {
      return obj.json as Document
    }

    if ("raw" in obj) {
      const raw = obj.raw as unknown

      if (isRichTextDocument(raw)) {
        return raw as Document
      }

      const parsedFromRaw = safeParse<Document>(raw)
      if (parsedFromRaw && isRichTextDocument(parsedFromRaw)) {
        return parsedFromRaw
      }
    }
  }

  // Raw string JSON
  if (typeof input === "string") {
    const parsed = safeParse<Document>(input)
    if (parsed && isRichTextDocument(parsed)) {
      return parsed
    }
  }

  return null
}

/** Type guard: is this a Contentful Rich Text Document? */
export function isRichTextDocument(value: unknown): value is Document {
  if (!value || typeof value !== "object") {
    return false
  }

  const v = value as Partial<Document>
  const isDoc = (v as any).nodeType === "document" && Array.isArray(v.content)
  return Boolean(isDoc)
}

/**
 * Convenience: convert any supported input to HTML.
 * Returns "" if input cannot be converted.
 */
export function richTextToHtml(input: unknown, options?: Options): string {
  const doc = ensureRichTextDocument(input)
  if (!doc) {
    return ""
  } else {
    return documentToHtmlString(doc, options)
  }
}

/** Safely JSON.parse a string. Returns undefined on failure. */
function safeParse<T = unknown>(maybeJson: unknown): T | undefined {
  if (typeof maybeJson !== "string") {
    return undefined
  }

  try {
    const parsed = JSON.parse(maybeJson) as T
    return parsed
  } catch {
    return undefined
  }
}
